import React from 'react';
import { connect } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';

// Store
import { RootState } from '../../store/store';
import { Dispatch } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';

// Utils
import { PASSWORD_RULES_HINT, getPasswordStrengthError } from '../../utils/passwordRules';

// Components
import IconMobileTailAdmin from '../../components/Icons/MobileTailAdmin/MobileTailAdmin';

// Hooks
import { useTranslation } from 'react-i18next';

type ResetPasswordProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ResetPassword: React.FC<ResetPasswordProps> = ({ isAuthenticated, resetPassword }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  // Logic
  if (isAuthenticated) {
    return <Navigate to={ROUTE.HOME} />;
  }

  if (!email) {
    return <Navigate to={ROUTE.AUTH_FORGOT_PASSWORD} />;
  }

  const formik = useFormik({
    initialValues: {
      code: '',
      password_digest: '',
      password_confirmation: '',
    },
    validationSchema: object({
      code: string().required('Required'),
      password_digest: string()
        .required('Required')
        .test('password-strength', function (value) {
          const error = getPasswordStrengthError(value || '');

          return error ? this.createError({ message: error }) : true;
        }),
      password_confirmation: string()
        .test('passwords-match', 'Passwords must match', function(value) {
          return value === this.parent.password_digest;
        })
        .required('Required'),
    }),
    onSubmit: async (values) => {
      try {
        await resetPassword({
          email,
          code: values.code,
          password: values.password_digest,
        });
        navigate(ROUTE.AUTH_SIGN_IN);
      } catch (error) {
        console.error('Failed to reset password:', error);
      }
    },
  });

  return (
    <div className="p-sign-in h-screen flex md:items-center md:justify-center md:p-6">
      <div className="p-sign-in__wrapper w-full md:w-auto grid rounded-sm items-center border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark md:grid-cols-[minmax(auto,_564px)_minmax(350px,_564px)] md:grid-rows-[600px]">
        <div className="p-sign-in__left-side flex justify-center items-center h-full w-full bg-primary">
          <IconMobileTailAdmin width="100%" height="100%" iconClassName='max-w-[389px] max-h-[350px] md:w-[389px] md:h-[350px]' />
        </div>
        <div className="p-sign-in__right-side flex flex-col w-full h-full py-13 px-11 border-stroke dark:border-strokedark xl:border-l-2">
          <div className='p-sign-in__text-form pb-9'>
            <span className="p-sign-in__greetings pb-1.5 block font-medium">
              {t('labels.cycle.auth.resetPassword')}
            </span>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('labels.cycle.auth.verificationCode')}
            </p>
          </div>
          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
            <div className="mb-4">
              <label htmlFor="code" className="mb-2.5 block font-medium text-black dark:text-white">
                {t('labels.cycle.auth.resetPassword')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={formik.values.code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('labels.cycle.auth.enterResetCode')}
                  className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  required
                />
                {formik.touched.code && formik.errors.code && (
                  <div style={{ color: 'red' }} className="text-sm mt-1 font-medium">{formik.errors.code}</div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="password_digest" className="mb-2.5 block font-medium text-black dark:text-white">
                {t('labels.cycle.auth.newPassword')}
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="password_digest"
                  name="password_digest"
                  value={formik.values.password_digest}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('labels.cycle.auth.enterPassword')}
                  className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  required
                />
                {formik.touched.password_digest && formik.errors.password_digest ? (
                  <div style={{ color: 'red' }} className="text-sm mt-1 font-medium">{formik.errors.password_digest}</div>
                ) : (
                  <div className="text-sm mt-1 text-gray-600 dark:text-gray-400">{PASSWORD_RULES_HINT}</div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="password_confirmation" className="mb-2.5 block font-medium text-black dark:text-white">
                {t('labels.cycle.auth.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  type="password"
                  id="password_confirmation"
                  name="password_confirmation"
                  value={formik.values.password_confirmation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t('labels.cycle.auth.confirmPassword')}
                  className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  required
                />
                {formik.touched.password_confirmation && formik.errors.password_confirmation && (
                  <div style={{ color: 'red' }} className="text-sm mt-1 font-medium">{formik.errors.password_confirmation}</div>
                )}
              </div>
            </div>
            <button
              type="submit"
              className="flex w-full justify-center rounded bg-primary p-3 font-medium text-white hover:bg-opacity-90"
            >
              {t('labels.cycle.auth.resetPassword')}
            </button>
            <div className="flex justify-center">
              <button
                onClick={() => navigate(ROUTE.AUTH_SIGN_IN)}
                className="text-sm text-primary hover:text-primary-dark transition-colors"
              >
                {t('labels.cycle.auth.backToSignIn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  isAuthenticated: state.auth?.isAuthenticated,
});

const mapDispatch = (dispatch: Dispatch) => ({
  resetPassword: dispatch.auth.confirmPasswordReset,
});

export default connect(mapState, mapDispatch)(ResetPassword); 